/**
 * Sistema de Correio (10.6R): mensagens in-game com recompensas coletáveis.
 * Fontes: bônus diário, marcos de boss, marcos de profundidade, avisos do sistema.
 * Diamantes são moeda premium ganha JOGANDO (NÃO é dinheiro real).
 */

import { GameConfig } from "../config/GameConfig";

export interface MailItemGift {
  defId: string;
  rarity: string; // Rarity; validado ao criar a instância
  level: number;
  /** 10.9J4 — item de teste do admin: ignora o nível mínimo pra equipar/usar na corrente. */
  vip?: boolean;
}
export interface MailReward {
  coins?: number;
  diamonds?: number;
  xp?: number;
  items?: MailItemGift[]; // 10.9FM — anexos: viram instâncias na bolsa ao coletar
  elixirs?: number; // 10.9K2 — Elixir de Proteção (forja) — baú semanal do login
}

/** 10.9K2 — a mensagem tem algo pra coletar? Único lugar que lista os campos (antes era repetido
 *  em 6 pontos; campo novo esquecido em um deles sumia do correio). */
export function hasMailReward(r: MailReward): boolean {
  return !!(r.coins || r.diamonds || r.xp || r.elixirs || r.items?.length);
}

export interface MailMessage {
  id: string;
  titleKey: string;
  bodyKey: string;
  bodyParams?: Record<string, string | number>;
  rewards: MailReward;
  sentAt: number;
  read: boolean;
  claimed: boolean;
}

export type MailSnapshot = Omit<MailMessage, never>[];

export class MailSystem {
  messages: MailMessage[] = [];
  private lastDailyDay = "";
  loginStreak = 0; // 10.9J8 — dias SEGUIDOS de login; reseta se pular 1 dia (ver checkDailyLogin)
  // 10.9Q — mails de UMA VEZ SÓ já enviados (welcome/boss/depth/level). Flag PERSISTENTE:
  // não depende da mensagem seguir na lista, então coletar/apagar/podar NÃO re-envia.
  // (Corrige o "bem-vindo com presente" voltando a cada atualização.)
  private sentKeys = new Set<string>();

  private nextId(): string {
    return `m_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  }

  /** true se este mail de-uma-vez-só ainda NÃO foi enviado (e o marca como enviado). */
  private once(key: string): boolean {
    if (this.sentKeys.has(key)) return false;
    this.sentKeys.add(key);
    return true;
  }

  send(titleKey: string, bodyKey: string, rewards: MailReward, bodyParams?: Record<string, string | number>): void {
    this.messages.unshift({
      id: this.nextId(), titleKey, bodyKey, bodyParams,
      rewards, sentAt: Date.now(), read: false, claimed: false,
    });
    if (this.messages.length > 50) this.messages.length = 50;
  }

  read(id: string): void {
    const m = this.messages.find(x => x.id === id);
    if (m) m.read = true;
  }

  claim(id: string): MailReward | null {
    const m = this.messages.find(x => x.id === id);
    if (!m || m.claimed) return null;
    m.claimed = true;
    m.read = true;
    return m.rewards;
  }

  markAllRead(): number {
    let n = 0;
    for (const m of this.messages) if (!m.read) { m.read = true; n++; }
    return n;
  }

  /** Apaga TODAS as mensagens lidas E já coletadas (ou sem recompensa). Retorna quantidade. */
  deleteAllRead(): number {
    const before = this.messages.length;
    this.messages = this.messages.filter(m => {
      // só apaga se estiver LIDA e (sem recompensa OU já coletada)
      return !(m.read && (!hasMailReward(m.rewards) || m.claimed));
    });
    return before - this.messages.length;
  }

  claimAll(): MailReward {
    const total: MailReward = {};
    for (const m of this.messages) {
      if (m.claimed) continue;
      if (!hasMailReward(m.rewards)) continue;
      m.claimed = true;
      m.read = true;
      if (m.rewards.coins) total.coins = (total.coins ?? 0) + m.rewards.coins;
      if (m.rewards.diamonds) total.diamonds = (total.diamonds ?? 0) + m.rewards.diamonds;
      if (m.rewards.xp) total.xp = (total.xp ?? 0) + m.rewards.xp;
      if (m.rewards.elixirs) total.elixirs = (total.elixirs ?? 0) + m.rewards.elixirs; // 10.9K2
      if (m.rewards.items?.length) total.items = [...(total.items ?? []), ...m.rewards.items]; // 10.9FM
    }
    return total;
  }

  sendLevelUp(level: number): void {
    // 10.9BB — Thiago: "a cada level ganha diamante e moeda" (fácil demais). Agora só MARCOS:
    // a cada 5 níveis (💎5) e a cada 10 (💎15); nível comum não manda correio.
    if (level % 5 !== 0) return;
    if (!this.once(`level:${level}`)) return;
    // 10.9BK — Thiago: subir de nível NÃO dá diamante. Só 🪙 (marco de 10 paga dobrado).
    const coins = level * (level % 10 === 0 ? 20 : 10);
    this.send("mail.levelUp.title", "mail.levelUp.body", { coins }, { level });
  }

  get unreadCount(): number {
    return this.messages.filter(m => !m.read).length;
  }

  get unclaimedCount(): number {
    return this.messages.filter(m => !m.claimed && hasMailReward(m.rewards)).length;
  }
  /** 10.9FM — envia UM anexo de itens (usado pela compensação e por prêmios futuros). */
  sendItems(titleKey: string, bodyKey: string, items: MailItemGift[], extra?: MailReward, bodyParams?: Record<string, string | number>): void {
    this.send(titleKey, bodyKey, { ...(extra ?? {}), items }, bodyParams);
  }

  /** Bônus diário de login — 1x por dia real.
   * 10.9J8 — ganhou SEQUÊNCIA: dia seguido sobe na tabela (GameConfig.dailyLogin); pular 1 dia
   * reseta pro dia 1. Do 7º dia em diante fica no valor do dia 7 enquanto a sequência durar.
   * 10.9K2 — BAÚ SEMANAL: a cada 7 dias seguidos (7, 14, 21…) chega um baú extra com 💎, 🪙 e
   * Elixir de Proteção — a 1ª fonte de Elixir que não é a loja. Antes o bônus só era conferido
   * ao abrir o app; agora o Game também confere ao voltar do 2º plano e a cada 90 s. */
  checkDailyLogin(vipDailyDiamonds = 0): boolean {
    const d = new Date();
    const fmt = (x: Date): string => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    const today = fmt(d);
    if (this.lastDailyDay === today) return false;
    const y = new Date(d); y.setDate(d.getDate() - 1);
    this.loginStreak = this.lastDailyDay === fmt(y) ? this.loginStreak + 1 : 1;
    this.lastDailyDay = today;
    const table = GameConfig.dailyLogin;
    const reward = table[Math.min(table.length, Math.max(1, this.loginStreak)) - 1];
    this.send("mail.daily.title", "mail.daily.body", { coins: reward.coins, diamonds: reward.diamonds }, { day: Math.min(table.length, this.loginStreak) });
    if (this.loginStreak > 0 && this.loginStreak % 7 === 0) {
      const w = GameConfig.weeklyLoginChest;
      this.send("mail.weekly.title", "mail.weekly.body", { diamonds: w.diamonds, coins: w.coins, elixirs: w.elixirs }, { weeks: this.loginStreak / 7 });
    }
    if (vipDailyDiamonds > 0) {
      this.send("mail.vipDaily.title", "mail.vipDaily.body", { diamonds: vipDailyDiamonds, coins: 20 });
    }
    return true;
  }

  /** Welcome mail (1ª vez — flag persistente, nunca re-envia). */
  sendWelcome(): void {
    if (!this.once("welcome")) return;
    this.send("mail.welcome.title", "mail.welcome.body", { diamonds: 10, coins: 50 });
  }

  /** Marco de boss (1ª derrota). */
  sendBossMilestone(bossKey: string): void {
    if (!this.once(`boss:${bossKey}`)) return;
    this.send(`mail.boss.${bossKey}.title`, `mail.boss.${bossKey}.body`, { coins: GameConfig.bossCoins.milestoneMail }); // 10.9BK: 🪙, não 💎
  }

  /** Marco de profundidade. */
  sendDepthMilestone(depth: number): void {
    if (!this.once(`depth:${depth}`)) return;
    this.send(`mail.depth.title`, "mail.depth.body", { diamonds: GameConfig.diamonds.perDepthMilestone, coins: 40 }, { depth }); // 10.9K4 — só aqui (o Game pagava de novo direto)
  }

  /** 10.9BA — prêmio de temporada (a claim única já é garantida no servidor). */
  sendSeasonPrize(season: number, category: string, rank: number, diamonds: number): void {
    this.send("mail.season.title", "mail.season.body", { diamonds }, { season, category, rank });
  }

  /** 10.9Q — chave única do mail de-uma-vez (pra semear a flag de players antigos). */
  private keyOf(m: MailMessage): string | null {
    if (m.titleKey === "mail.welcome.title") return "welcome";
    if (m.titleKey === "mail.levelUp.title" && typeof m.bodyParams?.level === "number") return `level:${m.bodyParams.level}`;
    if (m.titleKey === "mail.depth.title" && typeof m.bodyParams?.depth === "number") return `depth:${m.bodyParams.depth}`;
    const boss = /^mail\.boss\.(.+)\.title$/.exec(m.titleKey);
    if (boss) return `boss:${boss[1]}`;
    return null;
  }

  snapshot(): { messages: Record<string, unknown>[]; lastDailyDay: string; sentKeys: string[]; loginStreak: number } {
    return {
      messages: this.messages.map(m => ({ ...m }) as Record<string, unknown>),
      lastDailyDay: this.lastDailyDay,
      sentKeys: [...this.sentKeys], // 10.9Q
      loginStreak: this.loginStreak, // 10.9J8
    };
  }

  load(data: { messages?: Record<string, unknown>[]; lastDailyDay?: string; sentKeys?: string[]; loginStreak?: number } | undefined): void {
    this.messages = [];
    this.lastDailyDay = "";
    this.sentKeys = new Set();
    this.loginStreak = 0; // 10.9J8
    if (!data) return;
    if (Array.isArray(data.messages)) {
      for (const m of data.messages) {
        if (typeof m.id === "string" && typeof m.titleKey === "string") {
          this.messages.push({
            id: m.id,
            titleKey: m.titleKey,
            bodyKey: typeof m.bodyKey === "string" ? m.bodyKey : "",
            bodyParams: (m.bodyParams && typeof m.bodyParams === "object") ? m.bodyParams as Record<string, string | number> : undefined,
            rewards: (m.rewards && typeof m.rewards === "object") ? (m.rewards as MailReward) : {}, // 10.9FM inclui items
            sentAt: typeof m.sentAt === "number" ? m.sentAt : 0,
            read: m.read === true,
            claimed: m.claimed === true,
          });
        }
      }
    }
    if (typeof data.lastDailyDay === "string") this.lastDailyDay = data.lastDailyDay;
    this.loginStreak = Math.max(0, Math.min(999, Math.floor(data.loginStreak ?? 0))); // 10.9J8
    // 10.9Q — restaura a flag persistente + SEMEIA a partir das mensagens já na lista
    // (players antigos sem `sentKeys`: não re-envia o que ainda existe no correio).
    if (Array.isArray(data.sentKeys)) for (const k of data.sentKeys) if (typeof k === "string") this.sentKeys.add(k);
    for (const m of this.messages) { const k = this.keyOf(m); if (k) this.sentKeys.add(k); }
  }

  reset(): void {
    this.messages = [];
    this.lastDailyDay = "";
    this.sentKeys = new Set();
    this.loginStreak = 0; // 10.9J8
  }
}
